import { createContext, useContext, useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import { useAuth } from "./AuthContext";
import axiosInstance from "../config/axiosInstance";

const SOCKET_URL =
  process.env.REACT_APP_SOCKET_URL ||
  (process.env.NODE_ENV === "development"
    ? "http://localhost:3002"
    : "https://app.shopalli.com.br");

const ChatContext = createContext(null);

export const ChatProvider = ({ children }) => {
  const { token } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const socketRef = useRef(null);
  const isChatOpenRef = useRef(false);

  useEffect(() => {
    if (!token) return;

    const s = io(SOCKET_URL, {
      auth: { token },
      // Mantém o fallback de long-polling: se o proxy não fizer upgrade de
      // WebSocket, o chat continua funcionando por HTTP.
      transports: ["websocket", "polling"],
    });

    // Auto-join all existing conversation rooms so new_message events arrive
    s.on("connect", async () => {
      setConnected(true);
      try {
        const res = await axiosInstance.get("/chat/conversations");
        const conversations = res.data.conversations || [];
        conversations.forEach((conv) => {
          s.emit("join_conversation", { conversationId: conv.id });
        });
      } catch (err) {
        console.warn("[ChatContext] auto-join rooms failed:", err.message);
      }
    });

    s.on("disconnect", (reason) => {
      setConnected(false);
      console.warn("[ChatContext] socket desconectado:", reason);
    });

    // Sem isso a falha de handshake é silenciosa e o chat só "não funciona".
    s.on("connect_error", (err) => {
      setConnected(false);
      console.error(
        `[ChatContext] falha ao conectar em ${SOCKET_URL}:`,
        err.message
      );
    });

    s.on("new_message", ({ message }) => {
      if (message.senderType === "CUSTOMER" && !isChatOpenRef.current) {
        setUnreadCount((n) => n + 1);
      }
    });

    socketRef.current = s;
    setSocket(s);

    return () => {
      s.disconnect();
      socketRef.current = null;
      setSocket(null);
      setConnected(false);
    };
  }, [token]);

  const clearUnread = () => setUnreadCount(0);

  const setChatOpen = (open) => {
    isChatOpenRef.current = open;
    if (open) setUnreadCount(0);
  };

  return (
    <ChatContext.Provider
      value={{ socket, socketRef, connected, unreadCount, clearUnread, setChatOpen }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChatContext = () => useContext(ChatContext);
